import './PhoneInput.scss';

import { CaretDown, CaretUp } from '@phosphor-icons/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHandleClickOutside } from '../../../hooks/useHandleClickOutside';
import { useKeepScrollFor } from '../../../hooks/useKeepScroll';
import { useToggle } from '../../../hooks/useToggle';
import {
  countryCodes,
  CountryPrefix,
  countryPrefixes,
  transformToCountryPrefixedObject,
  usCountry,
} from '../../../utils';
import CountryFlag from '../../CountryFlag';

const validPhoneRegex = /^\+\d*$/;
const isValidPhoneNumber = (phoneNumber: string): boolean => {
  if (phoneNumber.length === 1) return phoneNumber === '+';

  return validPhoneRegex.test(phoneNumber);
};

/**
 * Function to get all the possible prefixes from a phone number
 * Filtering duplicates and empty prefixes
 * @param phoneNumber
 * @returns [+123, +12, +1]
 */
const getPhoneExtensionsFrom = (phoneNumber: string) => {
  const extensions = new Set(
    [
      phoneNumber.slice(0, 4),
      phoneNumber.slice(0, 3),
      phoneNumber.slice(0, 2),
    ].filter(Boolean),
  );

  return Array.from(extensions);
};

const addSpaceAfterPrefix = (phoneNumber: string, prefix: string) => {
  const startsWithPrefix = phoneNumber.startsWith(prefix);
  const isLongerThanPrefix = phoneNumber.length > prefix.length;

  if (startsWithPrefix && isLongerThanPrefix) {
    return phoneNumber.replace(prefix, `${prefix} `);
  }

  return phoneNumber;
};

const filterCountryCodesByPrefixesWhitelist = (whitelist: string[]) =>
  Object.entries(countryCodes).reduce(
    (acc, [code, { name, prefix }]) =>
      whitelist.includes(prefix) ? { ...acc, [code]: { name, prefix } } : acc,
    {} as typeof countryCodes,
  );

const countryFlagWidth = 30;
const countryFlagHeight = 20;

const phoneBaseClass = 'c-Phone';
const countryChoicesBaseClass = 'c-Phone-choices';

interface PhoneInputProps {
  onPhoneNumberChange?: (phoneNumber: string) => void;
  countryPrefixesWhitelist?: string[];
}

export const PhoneInput = ({
  onPhoneNumberChange,
  countryPrefixesWhitelist,
}: PhoneInputProps) => {
  const { t } = useTranslation('countries');

  const filteredCountryCodes = countryPrefixesWhitelist
    ? filterCountryCodesByPrefixesWhitelist(countryPrefixesWhitelist)
    : countryCodes;
  const filteredCountryPrefixes = countryPrefixesWhitelist
    ? transformToCountryPrefixedObject(filteredCountryCodes)
    : countryPrefixes;

  const [selectedCountry, setSelectedCountry] = useState(usCountry);
  const [hasCountry, setHasCountry] = useState(!!selectedCountry);
  const [phoneNumber, setPhoneNumber] = useState(usCountry.prefix);
  const [showCountries, { off: hideCountries, toggle: toggleCountries }] =
    useToggle();

  const countryChoicesRef = useRef<HTMLDivElement>(null);
  useHandleClickOutside(countryChoicesRef, hideCountries);

  useEffect(() => {
    if (onPhoneNumberChange) {
      onPhoneNumberChange(phoneNumber);
    }
  }, [phoneNumber]);

  const onPhoneInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = event.target.value.replace(' ', '');

    if (!isValidPhoneNumber(newNumber)) return;

    const phoneExtensions = getPhoneExtensionsFrom(newNumber);

    for (const extension of phoneExtensions) {
      const matchedCountries = filteredCountryPrefixes[extension];

      if (matchedCountries) {
        setHasCountry(true);
        const matchedCountryCodes = matchedCountries.map(
          (country) => country.code,
        );

        // This IF is to avoid conflicts for countries with the same prefix
        // For instance +64 is New Zealand and Pitcairn Islands
        // If the user types their number, we maintain the country
        if (matchedCountryCodes.includes(selectedCountry.code)) {
          setPhoneNumber(newNumber);
        } else {
          // If the user typed a new prefix, we find it with by the countries we have prefix matches
          const matchedCountry = filteredCountryPrefixes[extension].find(
            (country) => matchedCountryCodes.includes(country.code),
          );

          if (matchedCountry) {
            setSelectedCountry(matchedCountry);
            setPhoneNumber(matchedCountry.prefix);
          }
        }
        break;
      } else {
        setPhoneNumber(newNumber);
        setHasCountry(false);
      }
    }
  };

  const onCountryChoiceClick = ({ code, name, prefix }: CountryPrefix) => {
    setSelectedCountry({ code, name, prefix });

    if (selectedCountry.prefix !== prefix) {
      setPhoneNumber(prefix);
    }
    hideCountries();
  };

  const { handleScroll, scrollPosition } = useKeepScrollFor(countryChoicesRef);

  useEffect(() => {
    if (showCountries) {
      countryChoicesRef.current?.scrollTo(0, scrollPosition);
    }
  }, [showCountries, countryChoicesRef, scrollPosition]);

  return (
    <div>
      <div className={phoneBaseClass}>
        <div
          className={`${phoneBaseClass}-display`}
          onClick={toggleCountries}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') toggleCountries();
          }}
        >
          <CountryFlag
            width={countryFlagWidth}
            height={countryFlagHeight}
            countryCode={hasCountry ? selectedCountry.code.toLowerCase() : ''}
          />
          <span
            className={classNames(`${phoneBaseClass}-country-code-display`, {
              hide: !hasCountry,
            })}
          >
            {selectedCountry.code}
          </span>
          {showCountries ? <CaretUp /> : <CaretDown />}
        </div>
        <input
          className={`${phoneBaseClass}-phone-field`}
          onChange={onPhoneInputChange}
          type="tel"
          value={addSpaceAfterPrefix(phoneNumber, selectedCountry.prefix)}
        />
      </div>
      <div className={countryChoicesBaseClass}>
        {showCountries && (
          <div
            ref={countryChoicesRef}
            className={`${countryChoicesBaseClass}-container`}
            onScroll={handleScroll}
          >
            {Object.entries(filteredCountryCodes).map(
              ([code, { name, prefix }]) => (
                <div
                  key={code}
                  className={`${countryChoicesBaseClass}-item`}
                  onClick={() => onCountryChoiceClick({ code, name, prefix })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ')
                      onCountryChoiceClick({ code, name, prefix });
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span>
                    <CountryFlag
                      width={countryFlagWidth}
                      height={countryFlagHeight}
                      countryCode={code.toLowerCase()}
                    />
                  </span>
                  <span>{t(`countries.${code}`)}</span>
                  <span className={`${countryChoicesBaseClass}-item__prefix`}>
                    {prefix}
                  </span>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneInput;
